/* "Köp tillsammans" — adds the product and the checked add-ons to the cart in one request.
   Markup: snippets/product-bundle.liquid. */
if (!customElements.get('product-bundle')) {
  customElements.define(
    'product-bundle',
    class ProductBundle extends HTMLElement {
      constructor() {
        super();
        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.loading = false;
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('[data-bundle-submit]');
        this.buttonLabel = this.querySelector('[data-bundle-label]');
        this.spinner = this.button ? this.button.querySelector('.loading__spinner') : null;
        this.totalElement = this.querySelector('[data-bundle-total]');
        this.errorElement = this.querySelector('[data-bundle-error]');
        this.addons = Array.from(this.querySelectorAll('input[data-bundle-addon]'));

        this.addEventListener('change', this.onChange);
        if (this.button) this.button.addEventListener('click', this.onSubmit);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) =>
            this.onVariantChange(event)
          );
        }

        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        if (this.button) this.button.removeEventListener('click', this.onSubmit);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        this.variantChangeUnsubscriber = undefined;
      }

      onChange(event) {
        if (!event.target.matches('input[data-bundle-addon]')) return;
        this.setError();
        this.update();
      }

      /* Keep the main row in sync with the variant picker (Dawn publishes variantChange with the
         re-rendered section, which contains a fresh copy of this snippet). */
      onVariantChange(event) {
        const data = event && event.data;
        if (!data || String(data.sectionId) !== String(this.sectionId)) return;

        const fresh =
          data.html && typeof data.html.querySelector === 'function'
            ? data.html.querySelector(`product-bundle[data-section-id="${this.sectionId}"]`)
            : null;

        if (fresh) {
          this.dataset.mainVariantId = fresh.dataset.mainVariantId;
          this.dataset.mainPrice = fresh.dataset.mainPrice;
          this.dataset.mainAvailable = fresh.dataset.mainAvailable;
          ['[data-bundle-main-thumb]', '[data-bundle-main-label]'].forEach((selector) => {
            const current = this.querySelector(selector);
            const next = fresh.querySelector(selector);
            if (current && next) current.innerHTML = next.innerHTML;
          });
        } else if (data.variant) {
          this.dataset.mainVariantId = data.variant.id;
          this.dataset.mainPrice = data.variant.price;
          this.dataset.mainAvailable = data.variant.available ? 'true' : 'false';
          const mainPrice = this.querySelector('[data-bundle-main-label] .sv-bundle__price');
          if (mainPrice) mainPrice.textContent = this.formatMoney(Number(data.variant.price) || 0);
        } else {
          this.dataset.mainAvailable = 'false';
        }

        this.setError();
        this.update();
      }

      get mainAvailable() {
        return this.dataset.mainAvailable !== 'false';
      }

      get mainForm() {
        return document.getElementById(`product-form-${this.sectionId}`);
      }

      getMainVariantId() {
        const form = this.mainForm;
        const input = form ? form.querySelector('[name="id"]') : null;
        return (input && input.value) || this.dataset.mainVariantId;
      }

      /* Line item properties of the main product form (preorder Typ/_Leverans etc.). */
      getMainProperties() {
        const form = this.mainForm;
        const properties = {};
        if (!form) return properties;

        form.querySelectorAll('[name^="properties["]').forEach((input) => {
          if (input.disabled || input.value === '') return;
          if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
          const key = input.name.slice('properties['.length, -1);
          if (key) properties[key] = input.value;
        });
        return properties;
      }

      getAddonProperties(input) {
        if (input.dataset.preorder !== 'true') return {};
        const properties = { Typ: 'Förbeställning' };
        if (input.dataset.eta) properties._Leverans = input.dataset.eta;
        return properties;
      }

      update() {
        let total = Number(this.dataset.mainPrice) || 0;
        let units = 1;
        let allChecked = true;

        this.addons.forEach((input) => {
          const quantity = Number(input.dataset.quantity) || 1;
          const thumb = this.querySelector(`[data-bundle-thumb="${input.dataset.variantId}"]`);
          if (thumb) thumb.classList.toggle('is-unchecked', !input.checked);

          if (input.checked) {
            total += (Number(input.dataset.unitPrice) || 0) * quantity;
            units += quantity;
          } else {
            allChecked = false;
          }
        });

        if (this.totalElement) this.totalElement.textContent = this.formatMoney(total);

        if (!this.button || !this.buttonLabel) return;
        if (!this.mainAvailable) {
          this.buttonLabel.textContent = (window.variantStrings && window.variantStrings.soldOut) || 'Slutsåld';
          this.button.disabled = true;
          return;
        }

        this.button.disabled = false;
        if (allChecked) {
          this.buttonLabel.textContent = 'Lägg alla i varukorgen';
        } else {
          this.buttonLabel.textContent = `Lägg ${units} ${units === 1 ? 'vara' : 'varor'} i varukorgen`;
        }
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.loading || !this.button || this.button.disabled) return;

        const mainVariantId = this.getMainVariantId();
        if (!this.mainAvailable || !mainVariantId) {
          this.setError('Den valda varianten går inte att köpa just nu.');
          return;
        }

        const items = [
          { id: Number(mainVariantId), quantity: 1, properties: this.getMainProperties() },
        ];
        this.addons.forEach((input) => {
          if (!input.checked) return;
          items.push({
            id: Number(input.dataset.variantId),
            quantity: Number(input.dataset.quantity) || 1,
            properties: this.getAddonProperties(input),
          });
        });

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(this.button);
        }

        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('json')
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        const routes = window.routes || {};
        this.setError();
        this.setLoading(true);

        fetch(routes.cart_add_url || '/cart/add', config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-bundle',
                  productVariantId: mainVariantId,
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              this.setError(response.description || response.message || 'Något gick fel. Försök igen.');
              return;
            }

            if (!cart) {
              window.location = routes.cart_url || '/cart';
              return;
            }

            // A multi-item add returns { items, sections }; the cart notification needs a line key.
            const firstItem = Array.isArray(response.items) ? response.items[0] : null;
            if (firstItem) {
              if (!response.key) response.key = firstItem.key;
              if (!response.id) response.id = firstItem.id;
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-bundle',
                productVariantId: mainVariantId,
                cartData: response,
              });
            }

            cart.classList.remove('is-empty');
            cart.renderContents(response);
          })
          .catch((error) => {
            console.error(error);
            this.setError('Något gick fel. Försök igen.');
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      setLoading(loading) {
        this.loading = loading;
        if (!this.button) return;
        this.button.classList.toggle('loading', loading);
        this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
        if (loading) {
          this.button.setAttribute('aria-disabled', 'true');
        } else {
          this.button.removeAttribute('aria-disabled');
        }
        if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
      }

      setError(message = '') {
        if (this.errorElement) this.errorElement.textContent = message;
      }

      /* Formats cents with the shop's money format (same output as Liquid's `money`).
         Falls back to Intl when the visitor browses in another currency than the shop's. */
      formatMoney(cents) {
        const format = this.dataset.moneyFormat;
        const shopCurrency = this.dataset.shopCurrency;
        const activeCurrency =
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || this.dataset.currency;
        const placeholder = format ? format.match(/\{\{\s*(\w+)\s*\}\}/) : null;

        if (placeholder && (!activeCurrency || !shopCurrency || activeCurrency === shopCurrency)) {
          return format.replace(placeholder[0], this.formatAmount(cents, placeholder[1])).trim();
        }

        try {
          return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: activeCurrency || 'SEK',
            minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
          }).format(cents / 100);
        } catch (error) {
          return `${Math.round(cents / 100)} kr`;
        }
      }

      formatAmount(cents, type) {
        const withDelimiters = (precision, thousands, decimal) => {
          const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fraction ? `${grouped}${decimal}${fraction}` : grouped;
        };

        switch (type) {
          case 'amount_no_decimals':
            return withDelimiters(0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelimiters(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiters(0, '.', ',');
          case 'amount_with_space_separator':
            return withDelimiters(2, ' ', ',');
          case 'amount_no_decimals_with_space_separator':
            return withDelimiters(0, ' ', ',');
          case 'amount_with_apostrophe_separator':
            return withDelimiters(2, "'", '.');
          default:
            return withDelimiters(2, ',', '.');
        }
      }
    }
  );
}
